// Created By Cursor
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Language as WebsiteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  CheckCircle as LiveIcon,
  EditNote as DraftIcon,
  Dns as DomainIcon,
  Inbox as EnquiryIcon,
  OpenInNew as OpenIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  ViewCarousel as BannerIcon,
  Collections as GalleryIcon,
  Description as DocumentsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import { crmColors } from "../theme/colors";
import websiteApiService from "../services/websiteApiService";

const BRAND = crmColors.primary.main;
const ACCENT = crmColors.energy.main;

const HEADER_BTN_SX = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.78rem",
  minHeight: 34,
  px: 1.4,
  borderRadius: "10px",
  boxShadow: "none",
  transition: "transform 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease",
  "&:hover": { transform: "translateY(-1px)" },
};

/** Create Website button — teal accent */
const CREATE_BTN_SX = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.8rem",
  lineHeight: 1.2,
  minHeight: 38,
  height: 38,
  px: 1.5,
  py: 0,
  borderRadius: "9px",
  bgcolor: "#0d9488",
  color: "#fff",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    marginRight: 0.5,
    marginLeft: -0.1,
    "& > *:nth-of-type(1)": { fontSize: 17 },
  },
  "&:hover": {
    bgcolor: "#0f766e",
    boxShadow: "none",
  },
};

const goCreate = (navigate) => () => navigate("/manage-website/create");

const KPI_ICON_BG = ["#A26C02", "#5e35b1", "#00897b", "#E74900", "#455a64"];

const statusMeta = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "live" || s === "active") {
    return { color: crmColors.success.main, bg: alpha(crmColors.success.main, 0.12) };
  }
  if (s === "draft" || s === "inactive") {
    return { color: crmColors.warning.dark, bg: alpha(crmColors.warning.main, 0.14) };
  }
  if (s.includes("domain")) {
    return { color: BRAND, bg: alpha(BRAND, 0.12) };
  }
  if (s.includes("ssl")) {
    return { color: crmColors.error.main, bg: alpha(crmColors.error.main, 0.12) };
  }
  return { color: crmColors.text.secondary, bg: alpha("#94a3b8", 0.14) };
};

const publicSiteUrl = (row) => {
  const domain = String(row?.domain_name || "").trim();
  if (!domain || domain === "—") return null;
  if (/^https?:\/\//i.test(domain)) return domain;
  return `https://${domain.replace(/^\/+/, "")}`;
};

const KpiTile = ({ title, value, icon, index = 0, hint, onClick, active }) => {
  const iconBg = KPI_ICON_BG[index % KPI_ICON_BG.length];
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        appearance: "none",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.6,
        minHeight: 92,
        borderRadius: "14px",
        border: active ? `2px solid ${ACCENT}` : `1.5px solid ${alpha(iconBg, 0.22)}`,
        background: active
          ? `linear-gradient(165deg, #fff 0%, ${alpha(iconBg, 0.16)} 100%)`
          : `linear-gradient(165deg, #fff 0%, ${alpha(iconBg, 0.07)} 100%)`,
        boxShadow: active
          ? `0 10px 22px ${alpha(iconBg, 0.14)}`
          : `0 6px 16px ${alpha(iconBg, 0.07)}`,
        color: "inherit",
        textAlign: "left",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "inherit",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-2px)",
              boxShadow: `0 12px 24px ${alpha(iconBg, 0.14)}`,
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: iconBg,
          color: "#fff",
          boxShadow: `0 4px 10px ${alpha(iconBg, 0.32)}`,
          "& .MuiSvgIcon-root": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 800,
            color: "#64748b",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#94a3b8", mt: 0.2 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ManageWebsite = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    live: 0,
    draft: 0,
    domain_pending: 0,
    banners: 0,
    gallery: 0,
    documents: 0,
    enquiries: 0,
  });
  const [schools, setSchools] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [dashRes, listRes] = await Promise.all([
      websiteApiService.getWebsiteDashboard(),
      websiteApiService.getWebsiteSchools({
        q: appliedSearch,
        status: statusFilter,
        limit: 500,
      }),
    ]);

    if (dashRes.success && dashRes.data?.stats) {
      setStats((prev) => ({ ...prev, ...dashRes.data.stats }));
    } else if (!dashRes.success) {
      toast.error(dashRes.error || "Website dashboard API failed");
    }

    if (listRes.success) {
      setSchools(listRes.data || []);
    } else {
      setSchools([]);
      toast.error(listRes.error || "Website schools API failed");
    }
    setLoading(false);
  }, [appliedSearch, statusFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filtered = useMemo(() => schools, [schools]);

  const handleRefresh = () => {
    loadAll().then(() => toast.success("Website data refreshed"));
  };

  const openPublicSite = (row) => {
    const url = publicSiteUrl(row);
    if (!url) {
      toast.error("Domain not configured");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openSchoolDetail = (row) => {
    if (!row?.school_code) return;
    navigate(`/manage-website/${encodeURIComponent(row.school_code)}`);
  };

  return (
    <PageContentLayout
      title="Manage Website"
      subtitle="School public websites, content & domains"
      darkBar
      barSx={{ minHeight: 68, py: 1.75, px: 2.75 }}
      contentPadding={2}
      breadcrumbs={[
        {
          label: "Manage Website",
          icon: <WebsiteIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/dashboard"
      actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button
            variant="outlined"
            size="small"
            disableElevation
            startIcon={
              loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <RefreshIcon sx={{ fontSize: 15 }} />
            }
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              ...HEADER_BTN_SX,
              bgcolor: alpha("#fff", 0.08),
              borderColor: alpha("#fff", 0.4),
              color: "#fff",
              "&:hover": { bgcolor: alpha("#fff", 0.16), borderColor: "#fff" },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AddIcon />}
            onClick={goCreate(navigate)}
            sx={{
              ...CREATE_BTN_SX,
              bgcolor: "#14b8a6",
              border: `1px solid ${alpha("#fff", 0.55)}`,
              "&:hover": { bgcolor: "#0d9488" },
            }}
          >
            Create Website
          </Button>
        </Stack>
      }
    >
      {loading && schools.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: BRAND }} />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {/* Hero strip */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${alpha(BRAND, 0.16)}`,
              boxShadow: `0 12px 28px ${alpha(BRAND, 0.08)}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 2.75 },
                py: { xs: 2, md: 2.35 },
                background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.start} 45%, ${crmColors.hero.end} 100%)`,
                color: "#fff",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.75} alignItems="center">
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: alpha("#fff", 0.18),
                    border: `2px solid ${alpha("#fff", 0.35)}`,
                  }}
                >
                  <WebsiteIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.2 }}>
                    School websites
                  </Typography>
                  <Typography sx={{ opacity: 0.85, fontSize: "0.82rem", mt: 0.35, fontWeight: 600 }}>
                    Launch, manage domains, and edit public content for each school
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ display: { xs: "none", md: "flex" } }}>
                <Chip
                  icon={<BannerIcon sx={{ fontSize: "16px !important", color: "#fff !important" }} />}
                  label={`${stats.banners || 0} banners`}
                  size="small"
                  sx={{ bgcolor: alpha("#fff", 0.14), color: "#fff", fontWeight: 700, border: `1px solid ${alpha("#fff", 0.2)}` }}
                />
                <Chip
                  icon={<GalleryIcon sx={{ fontSize: "16px !important", color: "#fff !important" }} />}
                  label={`${stats.gallery || 0} gallery`}
                  size="small"
                  sx={{ bgcolor: alpha("#fff", 0.14), color: "#fff", fontWeight: 700, border: `1px solid ${alpha("#fff", 0.2)}` }}
                />
                <Chip
                  icon={<DocumentsIcon sx={{ fontSize: "16px !important", color: "#fff !important" }} />}
                  label={`${stats.documents || 0} docs`}
                  size="small"
                  sx={{ bgcolor: alpha("#fff", 0.14), color: "#fff", fontWeight: 700, border: `1px solid ${alpha("#fff", 0.2)}` }}
                />
              </Stack>
            </Box>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <KpiTile
              index={0}
              title="Total sites"
              value={stats.total}
              hint="All schools in website DB"
              icon={<SchoolIcon />}
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
            />
            <KpiTile
              index={1}
              title="Live"
              value={stats.live}
              hint="Active + domain set"
              icon={<LiveIcon />}
              active={statusFilter === "Live"}
              onClick={() => setStatusFilter("Live")}
            />
            <KpiTile
              index={2}
              title="Draft"
              value={stats.draft}
              hint="Inactive / unpublished"
              icon={<DraftIcon />}
              active={statusFilter === "Draft"}
              onClick={() => setStatusFilter("Draft")}
            />
            <KpiTile
              index={3}
              title="Domain pending"
              value={stats.domain_pending}
              hint="Needs domain"
              icon={<DomainIcon />}
              active={statusFilter === "Domain pending"}
              onClick={() => setStatusFilter("Domain pending")}
            />
            <KpiTile
              index={4}
              title="Enquiries"
              value={stats.enquiries}
              hint="Admission inbox"
              icon={<EnquiryIcon />}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(BRAND, 0.14)}`,
              overflow: "hidden",
              bgcolor: "#fff",
              boxShadow: `0 10px 24px ${alpha(BRAND, 0.05)}`,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
                borderBottom: `1px solid ${alpha(BRAND, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(BRAND, 0.08)} 0%, #fff 70%)`,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar sx={{ width: 34, height: 34, bgcolor: BRAND, color: "#fff" }}>
                  <SchoolIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0f172a" }}>
                    Website schools
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filtered.length} school{filtered.length === 1 ? "" : "s"}
                    {statusFilter !== "all" ? ` · ${statusFilter}` : ""}
                    {appliedSearch ? ` · “${appliedSearch}”` : ""}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  placeholder="Search name, code, domain…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setAppliedSearch(search.trim());
                  }}
                  sx={{
                    minWidth: { xs: "100%", sm: 260 },
                    "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#fff" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: BRAND }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  onClick={() => setAppliedSearch(search.trim())}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    bgcolor: BRAND,
                    px: 1.5,
                  }}
                >
                  Search
                </Button>
              </Stack>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        fontWeight: 800,
                        fontSize: "0.68rem",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        color: "#64748b",
                        bgcolor: alpha(BRAND, 0.04),
                        borderBottom: `1px solid ${alpha(BRAND, 0.1)}`,
                        py: 1.2,
                      },
                    }}
                  >
                    <TableCell>School</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 7 }}>
                        <Avatar
                          sx={{
                            width: 52,
                            height: 52,
                            mx: "auto",
                            mb: 1.25,
                            bgcolor: alpha(BRAND, 0.1),
                            color: BRAND,
                          }}
                        >
                          <WebsiteIcon />
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No websites yet</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto" }}>
                          Use <strong>Create Website</strong> in the top bar to add a school site.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((row) => {
                      const meta = statusMeta(row.site_status || row.status);
                      return (
                        <TableRow
                          key={`${row.school_code}-${row.id}`}
                          hover
                          onClick={() => openSchoolDetail(row)}
                          sx={{
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                            "&:hover": { bgcolor: alpha(BRAND, 0.04) },
                            "& td": {
                              py: 1.35,
                              borderBottom: `1px solid ${alpha("#94a3b8", 0.18)}`,
                            },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: alpha(BRAND, 0.12),
                                  color: BRAND,
                                  fontWeight: 800,
                                  fontSize: "0.85rem",
                                }}
                              >
                                {(row.school_name || "S").charAt(0).toUpperCase()}
                              </Avatar>
                              <Stack spacing={0.1}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                                  {row.school_name}
                                </Typography>
                                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8" }}>
                                  {row.school_code}
                                  {row.eduok_school_code ? ` · EduOK ${row.eduok_school_code}` : ""}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                              {row.domain_name || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.site_status || row.status || "—"}
                              sx={{
                                fontWeight: 700,
                                height: 24,
                                bgcolor: meta.bg,
                                color: meta.color,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {[
                                { k: "B", v: row.banner_count },
                                { k: "G", v: row.gallery_count },
                                { k: "D", v: row.document_count },
                                { k: "T", v: row.testimonial_count },
                              ].map((c) => (
                                <Chip
                                  key={c.k}
                                  size="small"
                                  label={`${c.k}${c.v ?? 0}`}
                                  sx={{
                                    height: 22,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    bgcolor: alpha(BRAND, 0.06),
                                    color: crmColors.text.secondary,
                                  }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                              {row.admin_name || "—"}
                              {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                              <Tooltip title="Open public site">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!publicSiteUrl(row)}
                                    onClick={() => openPublicSite(row)}
                                    sx={{ color: BRAND }}
                                  >
                                    <OpenIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Open details">
                                <IconButton
                                  size="small"
                                  onClick={() => openSchoolDetail(row)}
                                  sx={{
                                    color: "#fff",
                                    bgcolor: BRAND,
                                    borderRadius: "8px",
                                    width: 30,
                                    height: 30,
                                    "&:hover": { bgcolor: crmColors.primary.dark },
                                  }}
                                >
                                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </PageContentLayout>
  );
};

export default ManageWebsite;
